import React, {useCallback, useRef, useState, useEffect} from "react";
import {
	Box,
	Breadcrumbs,
	Button,
	Link,
	Typography,
	useTheme
} from "@mui/material";
import {useNavigate, useLocation} from "react-router-dom";
import SummarizeOutlined from "@mui/icons-material/SummarizeOutlined";
import PlayCircleOutlineOutlined from "@mui/icons-material/PlayCircleOutlineOutlined";
import {useRXDStateContext} from "@/contexts/rxdProgressContext";
import {Player, PlayerAPI} from "bitmovin-player/modules/bitmovinplayer-core";
import EngineBitmovinModule from "bitmovin-player/modules/bitmovinplayer-engine-bitmovin";
import MseRendererModule from "bitmovin-player/modules/bitmovinplayer-mserenderer";
import HlsModule from "bitmovin-player/modules/bitmovinplayer-hls";
import XmlModule from "bitmovin-player/modules/bitmovinplayer-xml";
import DashModule from "bitmovin-player/modules/bitmovinplayer-dash";
import AbrModule from "bitmovin-player/modules/bitmovinplayer-abr";
import ContainerTSModule from "bitmovin-player/modules/bitmovinplayer-container-ts";
import ContainerMp4Module from "bitmovin-player/modules/bitmovinplayer-container-mp4";
import SubtitlesModule from "bitmovin-player/modules/bitmovinplayer-subtitles";
import SubtitlesCEA608Module from "bitmovin-player/modules/bitmovinplayer-subtitles-cea608";
import PolyfillModule from "bitmovin-player/modules/bitmovinplayer-polyfill";
import StyleModule from "bitmovin-player/modules/bitmovinplayer-style";
import {UIFactory} from "bitmovin-player-ui";
import {LessonVideoPlayer} from "@/pages/ModulePage/LessonPage/LessonVideoPlayer";
import {ExpandableDescription} from "@/components/ExpandableDescription";
import {VideoPlayerRef} from "@buildwitt/shapeshift-common/v1/videoPlayer/videoPlayer";
import {bitmovinPlayerConfig, palette} from "@/constants";
import bgImage from "@/icons/Rusticibg.png";
import {ILesson} from "@buildwitt/shapeshift-types";
import {ICourseEntity} from "@buildwitt/shapeshift-types/entities";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import Check from "@mui/icons-material/Check";
import { useSSTranslation } from "@/i18n/useSSTranslation";

const button_style = {
	borderRadius: "32px",
	backgroundColor: palette.orange,
	color: palette.black,
	"&:hover": {
		backgroundColor: palette.orange,
		opacity: 0.9
	},
	"&:disabled": {
		backgroundColor: palette.RxdButtonDisabledBg,
		color: palette.RxdButtonDisabledText
	}
};

export const LessonDetail = () => {
	const {t} = useSSTranslation();
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const mq = useMq();
	const module: ILesson = location.state?.module;
	const course: ICourseEntity = location.state?.course;
	const modules: ILesson[] = location.state?.modules || [];
	const lessonNumber: number = location.state?.lessonNumber || 0;
	const courseSlug: string = location.state?.courseSlug || course?.slug;
	const bottomDivRef = useRef<HTMLDivElement>(null);
	
	// Create a unique key for this lesson to force re-render
	const lessonKey = `${courseSlug}-${module?.slug}-${lessonNumber}`;

	// RXD Progress Context
	const {
		rxdState,
		lessonMode,
		isNormalMode,
		shouldSaveProgress,
		saveSuspendData,
		loadSuspendData,
		updateModulePosition,
		updateVideoProgress
	} = useRXDStateContext();

	const videoRef = useRef<VideoPlayerRef | null>(null);
	const [player, setPlayer] = useState<PlayerAPI | null>(null);
	const [videoFinished, setVideoFinished] = useState(false);
	const [videoProgress, setVideoProgress] = useState(0);
	const [componentKey, setComponentKey] = useState(0);
	const [isLessonCompleted, setIsLessonCompleted] = useState(false);
	const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

	const setupBitmovinModules = () => {
		Player.addModule(EngineBitmovinModule);
		Player.addModule(MseRendererModule);
		Player.addModule(HlsModule);
		Player.addModule(XmlModule);
		Player.addModule(DashModule);
		Player.addModule(AbrModule);
		Player.addModule(ContainerTSModule);
		Player.addModule(ContainerMp4Module);
		Player.addModule(SubtitlesModule);
		Player.addModule(SubtitlesCEA608Module);
		Player.addModule(PolyfillModule);
		Player.addModule(StyleModule);
	};

	// Load suspend data on mount
	useEffect(() => {
		loadSuspendData();
	}, [loadSuspendData]);


	// Check if current lesson is already completed
	useEffect(() => {
		if (rxdState && lessonNumber >= 0) {
			const isCompleted = rxdState.modulePosition > lessonNumber;
			setIsLessonCompleted(isCompleted);
		}
	}, [rxdState, lessonNumber]);

	// Force complete component reset when lesson changes
	useEffect(() => {
		// Reset all video-related state
		setVideoFinished(false);
		setVideoProgress(0);
		setPlayer(null);
		
		// Clear video ref
		if (videoRef.current) {
			videoRef.current = null;
		}
		
		// Clear any intervals
		if (progressIntervalRef.current) {
			clearInterval(progressIntervalRef.current);
			progressIntervalRef.current = null;
		}
		
		// Force re-render
		setComponentKey(prev => prev + 1);
		forceScrollDown();
	}, [lessonNumber, module?.slug]);

	useEffect(() => {
		if (bottomDivRef.current && (videoFinished || isLessonCompleted)){
			forceScrollDown();
		}
	}, [videoFinished, isLessonCompleted])

	const forceScrollDown = () => {
		setTimeout(() => {
			if (bottomDivRef.current){
				bottomDivRef.current.scrollIntoView({
					behavior: "smooth",
					inline: "nearest"
				});
			}
		}, 1000);
	}


	// Cleanup video player on unmount
	useEffect(() => {
		if (!player) {
			return;
		}

		progressIntervalRef.current = setInterval(() => {
			if (player){
				const finished = (player.getDuration() - player.getCurrentTime()) < 5;
				if(finished) setVideoFinished(true);
			}
		}, 500);

		return () => {
			if (progressIntervalRef.current) {
				clearInterval(progressIntervalRef.current);
			}
		};
	}, [player]);

	// Handle video progress updates
	const handleVideoProgress = useCallback(async (progressData: { playedSeconds: number }) => {
		if (!shouldSaveProgress()) return;

		const currentTime = progressData.playedSeconds;
		setVideoProgress(currentTime);

		// Save progress every 10 seconds
		if (Math.floor(currentTime) % 10 === 0) {
			await updateVideoProgress(currentTime);
		}
	}, [shouldSaveProgress, updateVideoProgress]);

	// Navigation functions
	const handlePreviousLesson = () => {
		if (lessonNumber === 0) {
			// First lesson - go back to RXD course landing page
			goToMainPage();
		} else {
			// Go to previous lesson
			const previousModule = modules[lessonNumber - 1];
			if (previousModule) {
				navigate(`/module/${previousModule.slug}`, {
					state: { course, module: previousModule, lessonNumber: lessonNumber - 1, modules }
				});
			}
		}
	};

	const handleNextLesson = () => {
		if (lessonNumber < modules.length - 1) {
			// Go to next lesson
			const nextModule = modules[lessonNumber + 1];
			if (nextModule) {
				navigate(`/module/${nextModule.slug}`, {
					state: { course, module: nextModule, lessonNumber: lessonNumber + 1, modules }
				});
			}
		} else {
			// Last lesson - go back to RXD course landing page
			goToMainPage();
		}
	};

	const handleCompleteLesson = async () => {
		if (!shouldSaveProgress()) {
			// In browse/review mode, just navigate without saving progress
			handleNextLesson();
			return;
		}

		// If lesson is already completed, just navigate
		if (isLessonCompleted) {
			handleNextLesson();
			return;
		}

		// In normal mode, increment module position and save
		// modulePosition represents completed modules, so we increment by 1
		const currentPosition = rxdState?.modulePosition || 0;
		const newPosition = Math.max(currentPosition, lessonNumber + 1);
		await updateModulePosition(newPosition);
		
		// Update local completion state immediately
		setIsLessonCompleted(true);
		
		// Check if this is the last lesson
		if (lessonNumber >= modules.length - 1) {
			// Last lesson completed - navigate back to RXD course landing page
			goToMainPage();
		} else {
			// Navigate to next lesson
			handleNextLesson();
		}
	};

	const handleVideoReady = useCallback((lesson: ILesson) => {
		if (!process.env.REACT_APP_BITMOVIN_PLAYER_KEY) {
			throw new Error("REACT_APP_BITMOVIN_PLAYER_KEY required");
		}

		if (!videoRef.current?.bitmovin?.container) {
			// Retry after a short delay
			setTimeout(() => handleVideoReady(lesson), 200);
			return;
		}

		// Clean up existing player before creating new one
		if (player) {
			try {
				player.destroy();
			} catch (error) {
				// Player cleanup failed, continue with new player creation
			}
		}

		const playerSource = {
			hls: lesson.videoUrl,
			poster: lesson.videoThumbnailUrl
		};

		setupBitmovinModules();

		// Delay player setup for Safari and to ensure container is ready
		setTimeout(() => {
			const container = videoRef.current?.bitmovin?.container;
			if (!container) {
				return;
			}

			const playerInstance = new Player(container, bitmovinPlayerConfig);
			UIFactory.buildDefaultUI(playerInstance);

			playerInstance.load(playerSource).then(
				() => {
					setPlayer(playerInstance);
				},
				(e) => console.error("Error loading video source", e)
			);
		}, 200);
	}, [player]);

	const goToMainPage = () => {
		const localeQueryString = course.locale !== "en" ? `?locale=${course.locale}` : "";
		window.location.href = `/rxd/${courseSlug}${localeQueryString}`;
	}

	return (
		<Box
			sx={{
				backgroundImage: `url(${bgImage})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundColor: palette.RxdBackground,
				minHeight: "100vh",
				padding: "10px 32px 32px 32px"
			}}
		>
			<Breadcrumbs aria-label="breadcrumb" sx={{color: "#FFFFFFB2"}}>
				<Link
					underline="hover"
					color="inherit"
					component="button"
					onClick={() => goToMainPage()}
					sx={{display: "flex", alignItems: "center"}}
				>
					<SummarizeOutlined sx={{marginRight: "10px"}} fontSize="inherit" />
					{course?.title}
				</Link>
				<Link
					color="inherit"
					underline="none"
					sx={{display: "flex", alignItems: "center"}}
				>
					<PlayCircleOutlineOutlined sx={{marginRight: "10px"}} fontSize="inherit" />
					{module?.title}
				</Link>
			</Breadcrumbs>

			<Box 
				sx={{
					marginTop: "10px",
					position: "relative",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
				key={`video-container-${componentKey}`}
			>
				<Box
					sx={{
						width: "100%",
						maxWidth: "1200px",
						display: "flex",
						justifyContent: "flex-start",
						alignItems: "flex-start",
						flexDirection: "column"						
					}}
				>
					<LessonVideoPlayer
						key={`video-player-${lessonKey}-${componentKey}`}
						ref={videoRef}
						lesson={module}
						bitmovinPlayer={player}
						onReady={() => {
							module && handleVideoReady(module);
						}}
						onPlay={() => {}}
						onPause={() => {}}
						onEnded={() => {
							setVideoFinished(true);
						}}
						onProgress={handleVideoProgress}
					/>
					<Box sx={{marginTop: "10px"}}>
						<Typography
							variant="h4"
							sx={{
								marginBottom: "10px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis"
							}}
							color={theme.palette.primary.contrastText}
						>
							{module?.title}
						</Typography>
						<ExpandableDescription description={course?.description} />
					</Box>
					<Box 
						sx={{
							marginTop: "10px", 
							gap: "16px", 
							justifyContent: "space-between", 
							flexDirection: mq.mobile ? "column" : "row", 
							display: "flex", 
							width: "100%"
						}}
					>
						<Button
							variant="contained"
							onClick={handlePreviousLesson}
							data-test-id="previous-Lesson-button"
							aria-label="Previous Lesson"
							sx={button_style}
						>
							<ArrowBackIosNew sx={{fontSize: "16px", marginRight: "8px"}} />
							{lessonNumber === 0 ? t("rxd_back_to_course", "Back to Course") : t("rxd_previous_lesson", "Previous Lesson")}
						</Button>
						<Button
							disabled={!videoFinished && !isLessonCompleted}
							variant="contained"
							onClick={handleCompleteLesson}
							data-test-id="mark-as-completes-button"
							aria-label={lessonNumber < modules.length - 1 ? t("rxd_next_lesson", "Next Lesson") : t("rxd_complete_lesson", "Complete Lesson")}
							sx={button_style}
						>
							<Check sx={{fontSize: "16px", marginRight: "8px"}} />
							{lessonNumber < modules.length - 1 ? t("rxd_next_lesson", "Next Lesson") : t("rxd_complete_lesson", "Complete Lesson")}
						</Button>
					</Box>
					<Box ref={bottomDivRef} />
				</Box>
			</Box>
		</Box>
	);
};
